import React, { FC, useEffect, useState } from "react"
import CardTable from "../../../../ui/table/CardTable"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { User, UserAdvanceProgress } from "../../../../../types"
import { addCommasToNumber, mapListToCamelCase, replaceCurrencies } from "../../../../../utility"
import Pagination from "../../../../ui/table/Pagination"
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js"
import { Line } from "react-chartjs-2"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend)

const RoyaltyTypeOptionList: KSelectOption[] = [
  { label: "Total Amount", value: 1, value2: "total_amount" },
  { label: "Total Net", value: 2, value2: "total_net" }
]

const UserTypeOptionList: KSelectOption[] = [
  { label: "User", value: 1, value2: "user" },
  { label: "Total", value: 2, value2: "total" }
]

type AdminUserAdvanceProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const AdminUserAdvance: FC<AdminUserAdvanceProps> = (props) => {
  const [userOptions, setUserOptions] = useState<KSelectOption[]>([])
  const [searchUser, setSearchUser] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userAdvanceProgressList, setUserAdvanceProgressList] = useState<UserAdvanceProgress[]>([])
  const [count, setCount] = useState(0)
  const [selectedRoyaltyType, setSelectedRoyaltyType] = useState<KSelectOption>(RoyaltyTypeOptionList[0])
  const [selectedUserType, setSelectedUserType] = useState<KSelectOption>(UserTypeOptionList[0])

  const fetchUserAdvanceProgress = () => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    users(
      `/user_advance_progress/?offset=${offset}&limit=${limit}${userIdParam}&royalty_type=${selectedRoyaltyType.value2}&user_type=${selectedUserType.value2}`,
      "GET"
    ).then((resp) => {
      setUserAdvanceProgressList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
    })
  }

  useEffect(() => {
    fetchUserAdvanceProgress()
  }, [offset, selectedUser, selectedRoyaltyType, selectedUserType])

  const onSearchTextChanged = async (text: string) => {
    if (!text) return
    setUserOptions([])
    clearTimeout(timeoutId)
    setTimeoutId(
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
  }

  const renderEstimatedRecoupment = (
    advanceRecoupTracker: any,
    advanceEstimateRecoupTracker: any,
    month: string,
    recoupMonthTotal: number
  ) => {
    if ((advanceEstimateRecoupTracker[month] || 99999) > recoupMonthTotal) {
      return (
        <div>
          <KSpan text={`Total Recoup: ${recoupMonthTotal} months`} />
        </div>
      )
    }
    return (
      <div>
        {advanceEstimateRecoupTracker[month] && parseInt(advanceEstimateRecoupTracker[month]) < 100 && (
          <KSpan
            text={`Est. / Total Recoup: ${advanceEstimateRecoupTracker[month]} / ${
              monthsBetween(month, advanceRecoupTracker[month]) + parseInt(advanceEstimateRecoupTracker[month])
            } months`}
          />
        )}
      </div>
    )
  }

  const monthsBetween = (start: string, end: string) => {
    if (!start || !end) {
      return 0
    }
    const startYear = parseInt(start.slice(0, 4))
    const startMonth = parseInt(start.slice(4, 6))

    const endYear = parseInt(end.slice(0, 4))
    const endMonth = parseInt(end.slice(4, 6))

    return (endYear - startYear) * 12 + (endMonth - startMonth)
  }

  const renderUserAdvance = () => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {userAdvanceProgressList.map((userProgress) => {
          const { user, advances, royalties, currency, irrAnnual, irrMonthly, projections } = userProgress

          // Track advance recoup status
          const advanceTracker: { [key: string]: boolean } = {}
          const advanceRecoupTracker: { [key: string]: string } = {}
          const advanceRemainingTracker: { [key: string]: number } = {}
          const advanceEstimateRecoupTracker: { [key: string]: string } = {}

          let advanceTotal = 0
          let royaltyTotal = 0
          Object.entries(royalties).forEach(([royaltyMonth, royaltyAmount]) => {
            royaltyTotal += royaltyAmount
          })

          // Sort advances by month to process them in chronological order
          const sortedAdvances = Object.entries(advances).sort(([a], [b]) => a.localeCompare(b))
          const months = Object.keys(royalties).sort()

          // Track cumulative advances - royalties "pay off" advances in order
          let cumulativeAdvances = 0

          sortedAdvances.forEach(([advMonth, advAmount]) => {
            advanceTotal += advAmount
            cumulativeAdvances += advAmount

            let cumulativeRoyalty = 0
            let recouped = false

            for (let i = 0; i < months.length; i++) {
              const month = months[i]
              cumulativeRoyalty += royalties[month] || 0

              // Check if cumulative royalties >= cumulative advances (including this advance)
              if (cumulativeRoyalty >= cumulativeAdvances) {
                advanceTracker[advMonth] = true // advance recouped
                advanceRecoupTracker[advMonth] = month
                advanceRemainingTracker[advMonth] = 0
                advanceEstimateRecoupTracker[advMonth] = "0"
                recouped = true
                break
              }
            }

            if (!recouped) {
              advanceTracker[advMonth] = false
              advanceRecoupTracker[advMonth] = months[months.length - 1] || advMonth
              advanceRemainingTracker[advMonth] = cumulativeAdvances - cumulativeRoyalty
              // Estimate based on last month's royalty
              const lastMonth = months[months.length - 2]
              if (lastMonth && royalties[lastMonth] > 0) {
                advanceEstimateRecoupTracker[advMonth] = Math.ceil(
                  advanceRemainingTracker[advMonth] / royalties[lastMonth]
                ).toString()
              }
            }
          })

          // Track which months are recoup milestones (using cumulative advances logic)
          const recoupedMonthTracker: { [month: string]: boolean } = {}
          let cumulativeAdvancesForRecoup = 0
          sortedAdvances.forEach(([advMonth, advAmount]) => {
            cumulativeAdvancesForRecoup += advAmount
            let cumulativeRoyalty = 0
            for (const month of months) {
              cumulativeRoyalty += royalties[month] || 0
              if (cumulativeRoyalty >= cumulativeAdvancesForRecoup) {
                recoupedMonthTracker[month] = true // mark this month as the one where advance is recouped
                break
              }
            }
          })

          const allMonths = Object.keys({ ...advances, ...royalties, ...projections }).sort()

          const realMonths = Object.keys({ ...advances, ...royalties }).sort()
          const projectionMonths = Object.keys(projections).sort()

          let cumulative = 0
          const realChartData: number[] = []

          realMonths.forEach((month) => {
            const royalty = royalties[month] || 0
            const advance = advances[month] || 0
            cumulative += royalty - advance
            realChartData.push(cumulative)
          })

          const projectionChartData: number[] = []

          projectionMonths.forEach((month) => {
            const projValue = projections[month] || 0
            cumulative += projValue // continue cumulative forward
            projectionChartData.push(cumulative)
          })

          const labels = [...realMonths, ...projectionMonths]

          // 1️⃣ Pad projectionChartData so it aligns after real months
          const paddedProjectionData = [
            ...Array(realMonths.length).fill(null), // fill the beginning with nulls
            ...projectionChartData // actual projection values
          ]

          // 2️⃣ Real data stays as is
          const paddedRealData = [
            ...realChartData,
            ...Array(projectionMonths.length).fill(null) // pad after last real month if needed
          ]

          const data = {
            labels,
            datasets: [
              {
                label: "Balance",
                data: paddedRealData,
                borderColor: "#4F46E5",
                segment: {
                  backgroundColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "rgba(24, 154, 35, 0.3)" : "rgba(107,114,128,0.3)"
                  },
                  borderColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "#189A23" : "#6B6E80"
                  }
                },
                fill: true,
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6
              },
              {
                label: "Projection",
                data: paddedProjectionData,
                borderColor: "#FF9800", // DIFFERENT color
                fill: true,
                borderDash: [6, 4], // dashed line
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6,
                segment: {
                  backgroundColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "rgba(24, 154, 35, 0.3)" : "rgba(107,114,128,0.3)"
                  },
                  borderColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "#189A23" : "#6B6E80"
                  }
                }
              }
            ]
          }

          const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { mode: "index" as const, intersect: false },
              datalabels: { display: false }
            },
            scales: {
              x: {
                title: { display: false, text: "Month" }
              },
              y: {
                title: { display: false, text: "Amount" },
                beginAtZero: true
              }
            }
          }

          return (
            <div
              key={`user_advance_progress_${userProgress.userId}`}
              className="flex flex-col gap-4"
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px"
              }}
            >
              {/* User Info + Advances + Royalties */}
              <div className="flex" style={{ width: "100%" }}>
                {/* User Info */}
                <div style={{ width: "20%" }}>
                  {selectedUserType.value2 === "user" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <img
                        src={user?.photo || "/account_icons/avatar.svg"}
                        alt={user?.firstName}
                        style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                      />
                      <div>
                        <div>
                          <KTitleSpan fontSize={16} lineHeight="20px" text={`${user?.firstName} ${user?.lastName}`} />
                        </div>
                        <div>
                          <KSpan fontSize={12} text={user?.email} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Advances */}
                  <div style={{ marginTop: "12px" }}>
                    <div>
                      <KTitleSpan fontSize={16} lineHeight="20px" text="Advances:" />
                    </div>
                    {Object.entries(advances).map(([month, amount]) => (
                      <div className={selectedUserType.value2 === "user" ? "mb-6" : "mb-1"}>
                        <div
                          key={`advance_${month}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "4px"
                          }}
                        >
                          <div>
                            <KSpan text={`${month}:`} />
                          </div>
                          <div>
                            <KSpan text={`${addCommasToNumber(amount.toFixed(2))} ${replaceCurrencies(currency)}`} />
                          </div>
                          {selectedUserType.value2 === "user" && (
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: advanceTracker[month] ? "green" : "red"
                              }}
                            />
                          )}
                        </div>
                        {selectedUserType.value2 === "user" && (
                          <div className="mt-1">
                            {advanceRemainingTracker[month] === 0 ? (
                              <KSpan
                                color="green"
                                text={`Recouped: ${monthsBetween(month, advanceRecoupTracker[month])} months`}
                              />
                            ) : (
                              <div className="grid gap-1">
                                <KSpan
                                  text={`Remaining: ${addCommasToNumber(
                                    advanceRemainingTracker[month]?.toFixed(2)
                                  )} ${replaceCurrencies(currency)}`}
                                />
                                {renderEstimatedRecoupment(
                                  advanceRecoupTracker,
                                  advanceEstimateRecoupTracker,
                                  month,
                                  userProgress.recoupMonthTotal
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-col">
                    <KSpan text={`IRR Monthly: ${irrMonthly?.toFixed(5)}`} />
                    <KSpan text={`IRR Annually: ${irrAnnual?.toFixed(5)}`} />
                  </div>
                </div>

                {/* Royalties */}
                <div className="ml-8" style={{ width: "80%" }}>
                  <div className="mb-2">
                    <KTitleSpan fontSize={16} lineHeight="20px" text="Royalties:" />
                  </div>
                  <div className="flex overflow-x-auto pb-4" style={{ width: "100%" }}>
                    {Object.entries(royalties)
                      .sort(([a], [b]) => (a > b ? 1 : -1))
                      .map(([month, amount]) => (
                        <div key={`royalty_${month}`} className="grid gap-1 flex-shrink-0" style={{ width: 100 }}>
                          <KSpan color={recoupedMonthTracker[month] ? "green" : "black"} text={month} />
                          <KSpan
                            color={recoupedMonthTracker[month] ? "green" : "black"}
                            text={`${addCommasToNumber(amount.toFixed(2))} ${replaceCurrencies(currency)}`}
                          />
                        </div>
                      ))}
                  </div>
                  {/* Chart.js Line Chart */}
                  <div className="pt-4" style={{ width: "100%", height: 250 }}>
                    <Line data={data} options={options} />
                  </div>
                  <div className="pt-8 flex gap-12">
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Advance Total: ${addCommasToNumber(advanceTotal.toFixed(2))} ${replaceCurrencies(
                        currency
                      )}`}
                    />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Royalty Total: ${addCommasToNumber(royaltyTotal.toFixed(2))} ${replaceCurrencies(
                        currency
                      )}`}
                    />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Remaining Total: ${
                        royaltyTotal < advanceTotal
                          ? `${addCommasToNumber((advanceTotal - royaltyTotal).toFixed(2))} ${replaceCurrencies(
                              currency
                            )}`
                          : "-"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"User Advance"} />
          </div>
        </KTooltip>
        <div className="flex gap-2">
          <div>
            <KDropdown
              width={180}
              selected={selectedRoyaltyType}
              options={RoyaltyTypeOptionList}
              onSelect={(selected) => setSelectedRoyaltyType(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={180}
              selected={selectedUserType}
              options={UserTypeOptionList}
              onSelect={(selected) => setSelectedUserType(selected as KSelectOption)}
            />
          </div>
          {selectedUserType.value2 === "user" && (
            <div>
              {searchUser ? (
                <div
                  className="relative"
                  style={{ width: 250 }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setSearchUser(false)
                    }
                  }}
                >
                  <KDropdown
                    placeholder={"Search user to filter"}
                    width={240}
                    isClearable
                    selected={selectedUser}
                    onInputChange={onSearchTextChanged}
                    options={userOptions}
                    onSelect={(selectedOption) => {
                      setSelectedUser(selectedOption as KSelectOption)
                    }}
                  />
                </div>
              ) : (
                <div className="">
                  <KButton
                    leftIcon="/reports_icons/user.svg"
                    gap="4px"
                    text="Search user"
                    height="36px"
                    width="auto"
                    background="#F7F7F7"
                    hoverBackground="#F3F3F3"
                    activeBackground="#F7F7F7"
                    padding="8px"
                    shadowDisabled
                    onClick={() => {
                      setSearchUser(true)
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {renderUserAdvance()}
      {selectedUserType.value2 === "user" && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
          />
        </div>
      )}
    </div>
  )
}

export default AdminUserAdvance
